import { inject } from '@angular/core';
import {
    HttpRequest,
    HttpHandlerFn,
    HttpEvent,
    HttpInterceptorFn,
    HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { TokenService } from '../services/token.service';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../../environments/environment';

// State variables for tracking refresh token progress
let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const tokenService = inject(TokenService);
    const authService = inject(AuthService);

    // We only want to intercept API calls, optionally filter by URL
    // We also don't want to intercept the auth server calls themselves
    const isApiUrl = !req.url.startsWith(environment.authUrl);

    let authReq = req;
    const token = tokenService.getAccessToken();

    if (isApiUrl && token) {
        authReq = addTokenHeader(req, token);
    }

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // Don't intercept auth server 401s to avoid infinite loops
            if (error.status === 401 && isApiUrl) {
                return handle401Error(authReq, next, authService, tokenService);
            }
            return throwError(() => error);
        })
    );
};

function addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
        headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
}

function handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    tokenService: TokenService
): Observable<HttpEvent<unknown>> {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        const refreshToken = tokenService.getRefreshToken();
        if (refreshToken) {
            return authService.refreshToken().pipe(
                switchMap((response: any) => {
                    isRefreshing = false;
                    refreshTokenSubject.next(response.access_token);
                    return next(addTokenHeader(request, response.access_token));
                }),
                catchError((err) => {
                    isRefreshing = false;
                    authService.handleSessionExpired();
                    return throwError(() => err);
                })
            );
        } else {
            isRefreshing = false;
            authService.handleSessionExpired();
            return throwError(() => new Error('Refresh token not available'));
        }
    } else {
        // If a refresh is already in progress, wait until it finishes, then retry
        return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(token => {
                return next(addTokenHeader(request, token));
            })
        );
    }
}
